import { useEffect, useState, type ReactNode } from 'react';
import { ColumnDef } from '@/components/data-table/DataTable';
import { AnchoredDropdownMenu } from '@/components/ui/AnchoredDropdownMenu';
import { Tooltip } from '@/components/ui/Tooltip';
import { maskPhone, maskCPF } from '@/utils/masks';
import { formatDateWithDifference } from '@/utils/transformer';
import { cn } from '@/lib/utils';
import {
	Settings,
	CircleCheck,
	MoreVertical,
	Check,
	Info,
	X,
} from 'lucide-react';

export interface UserColumnsProps {
	onEdit: (id: string) => void;
	onToggleStatus: (ban: boolean, username: string, id: string) => void;
	clubs?: Clubs.Club[];
}

const ICON_COLUMN_HEADER_CLASS =
	'w-[5.75rem] min-w-[5.75rem] max-w-[5.75rem] px-1.5 text-center whitespace-normal leading-[1.15] align-middle';
const ICON_COLUMN_CELL_CLASS =
	'w-[5.75rem] min-w-[5.75rem] max-w-[5.75rem] px-1.5 text-center cursor-default';

const PATIENT_COLUMN_HEADER_CLASS =
	'w-[9.5rem] min-w-[9.5rem] max-w-[9.5rem] px-2';
const PATIENT_COLUMN_CELL_CLASS =
	'w-[9.5rem] min-w-[9.5rem] max-w-[9.5rem] px-2 cursor-text';

const TEXT_COLUMN_HEADER_CLASS =
	'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-2';
const TEXT_COLUMN_CELL_CLASS =
	'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-2 cursor-text';

const WIDE_TEXT_COLUMN_HEADER_CLASS = 'w-[7rem] min-w-[7rem] max-w-[7rem] px-2';
const WIDE_TEXT_COLUMN_CELL_CLASS =
	'w-[7rem] min-w-[7rem] max-w-[7rem] px-2 cursor-text';

const IconColumnCell = ({ children }: { children: ReactNode }) => (
	<div className="flex w-full items-center justify-center">{children}</div>
);

const resolveUserInitial = (row: Users.User) => {
	const source =
		row.first_name?.trim() ||
		row.last_name?.trim() ||
		row.email?.trim()?.charAt(0) ||
		'?';
	return source.charAt(0).toUpperCase();
};

const UserAvatar = ({ row }: { row: Users.User }) => {
	const [failed, setFailed] = useState(false);
	const initial = resolveUserInitial(row);
	const photoUrl = row.profile_photo_url?.trim();

	useEffect(() => {
		setFailed(false);
	}, [photoUrl]);

	if (photoUrl && !failed) {
		return (
			<img
				src={photoUrl}
				alt=""
				referrerPolicy="no-referrer"
				onError={() => setFailed(true)}
				className="mr-0 h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-zinc-200/80"
			/>
		);
	}

	return (
		<div
			className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-[11px] font-bold uppercase text-zinc-600 ring-1 ring-zinc-200/80"
			aria-hidden
		>
			{initial}
		</div>
	);
};

const CheckStatusBadge = ({
	enabled,
	label,
}: {
	enabled: boolean;
	label: string;
}) => (
	<span
		className={cn(
			'inline-flex size-7 cursor-help items-center justify-center rounded-full',
			enabled
				? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100'
				: 'bg-stone-100 text-stone-300 ring-1 ring-stone-200/80',
		)}
		aria-label={label}
		title={label}
	>
		{enabled ? (
			<Check className="size-3.5" strokeWidth={2.75} aria-hidden />
		) : (
			<X className="size-3.5" strokeWidth={2.75} aria-hidden />
		)}
	</span>
);

const ColumnHeaderWithHint = ({
	label,
	hint,
}: {
	label: string;
	hint: string;
}) => (
	<span className="inline-flex max-w-full flex-col items-center justify-center gap-0.5">
		<span className="inline-flex items-center justify-center gap-0.5">
			<span className="text-center">{label}</span>
			<Tooltip content={hint} position="top">
				<button
					type="button"
					className="inline-flex shrink-0 cursor-help rounded-full text-stone-400 transition-colors hover:text-stone-600"
					aria-label={hint}
				>
					<Info
						className="size-3 shrink-0"
						strokeWidth={2.25}
						aria-hidden
					/>
				</button>
			</Tooltip>
		</span>
	</span>
);

const ActionsDropdown = ({
	row,
	onEdit,
	onToggleStatus,
}: UserColumnsProps & { row: Users.User }) => {
	const [isOpen, setIsOpen] = useState(false);
	const closeMenu = () => setIsOpen(false);

	return (
		<AnchoredDropdownMenu
			open={isOpen}
			onOpenChange={setIsOpen}
			menuClassName="animate-in fade-in zoom-in-95 duration-100"
			trigger={({ ref, isOpen: menuOpen, onTriggerClick }) => (
				<button
					ref={ref}
					type="button"
					onClick={onTriggerClick}
					className={cn(
						'flex h-8 w-8 cursor-pointer items-center justify-center rounded-xl border border-solid outline-none transition focus:outline-none',
						menuOpen
							? 'border-zinc-500 bg-zinc-100 text-zinc-900'
							: 'border-stone-300 bg-white text-zinc-600 hover:border-zinc-500',
					)}
					aria-label="Abrir menu de ações"
					aria-expanded={menuOpen}
					aria-haspopup="menu"
				>
					<MoreVertical size={18} strokeWidth={3} />
				</button>
			)}
		>
			<div className="flex flex-col py-2">
				{!row.banned && (
					<button
						type="button"
						onClick={() => {
							closeMenu();
							onEdit(row.id);
						}}
						className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-700 outline-none transition hover:bg-stone-100"
					>
						<Settings
							size={18}
							className="shrink-0 text-stone-700"
						/>
						Configurar usuário
					</button>
				)}
				<button
					type="button"
					onClick={() => {
						closeMenu();
						onToggleStatus(
							!row.banned,
							`${row.first_name} ${row.last_name}`,
							row.id,
						);
					}}
					className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-700 outline-none transition hover:bg-stone-100"
				>
					<CircleCheck
						size={18}
						className="shrink-0 text-stone-700"
					/>
					{row.banned ? 'Reativar usuário' : 'Banir usuário'}
				</button>
			</div>
		</AnchoredDropdownMenu>
	);
};

const UserColumns = ({
	onEdit,
	onToggleStatus,
	clubs = [],
}: UserColumnsProps): ColumnDef<Users.User, keyof Users.User>[] => {
	const clubById = Object.fromEntries(clubs.map((c) => [c.id, c]));

	return [
		{
			header: (
				<ColumnHeaderWithHint
					label="Ativo"
					hint="Paciente ativo na plataforma"
				/>
			),
			accessor: 'banned',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (value) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={!value}
						label={value ? 'Banido' : 'Ativo'}
					/>
				</IconColumnCell>
			),
		},
		{
			header: 'Paciente',
			accessor: 'first_name',
			headerClassName: PATIENT_COLUMN_HEADER_CLASS,
			cellClassName: PATIENT_COLUMN_CELL_CLASS,
			cell: (_, row) => {
				const fullName =
					[row.first_name, row.last_name]
						.filter(Boolean)
						.join(' ')
						.trim() || '—';
				const email = row.email || '—';
				return (
					<div className="flex min-w-0 items-center gap-2">
						<UserAvatar row={row} />
						<div className="min-w-0 truncate">
							<p
								className="truncate text-xs font-medium text-dark"
								title={fullName}
							>
								{fullName}
							</p>
							<p
								className="truncate text-[11px] text-gray-500"
								title={email}
							>
								{email}
							</p>
						</div>
					</div>
				);
			},
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="Cadastro"
					hint="Estado do cadastro do paciente"
				/>
			),
			accessor: 'is_complete',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (value) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={!!value}
						label={
							value ? 'Cadastro completo' : 'Cadastro incompleto'
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="Google"
					hint="Permissões concedidas no Google"
				/>
			),
			accessor: 'google_permissions_allowed',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (value) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={!!value}
						label={
							value
								? 'Permissões Google concedidas'
								: 'Permissões Google não concedidas'
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: 'Empresa',
			accessor: 'employee',
			headerClassName: WIDE_TEXT_COLUMN_HEADER_CLASS,
			cellClassName: WIDE_TEXT_COLUMN_CELL_CLASS,
			cell: (_, row) => {
				const companyName =
					row.employee?.company?.trade_name?.trim() ||
					row.employee?.company?.legal_name?.trim() ||
					row.employee?.company?.name?.trim();
				return (
					<span
						className={cn(
							'block truncate text-xs',
							companyName ? 'text-dark' : 'italic text-gray-500',
						)}
						title={companyName || 'Sem empresa'}
					>
						{companyName || '—'}
					</span>
				);
			},
		},
		{
			header: 'Clube',
			accessor: 'user_club_subscription',
			headerClassName: WIDE_TEXT_COLUMN_HEADER_CLASS,
			cellClassName: WIDE_TEXT_COLUMN_CELL_CLASS,
			cell: (_, row) => {
				const subs = row.user_club_subscription ?? [];
				const paidNotCancelled = subs.filter(
					(s) => s.status === 'ACTIVE',
				);
				const byNewest = [...paidNotCancelled].sort((a, b) => {
					const ta = a.created_at
						? new Date(a.created_at).getTime()
						: 0;
					const tb = b.created_at
						? new Date(b.created_at).getTime()
						: 0;
					return tb - ta;
				});
				const seenClub = new Set<string>();
				const clubNames = byNewest
					.map((s) => {
						if (seenClub.has(s.club_id)) return null;
						seenClub.add(s.club_id);
						return (
							s.club?.name ?? clubById[s.club_id]?.name ?? null
						);
					})
					.filter((n): n is string => Boolean(n));
				const label = clubNames.length > 0 ? clubNames.join(', ') : '—';
				return (
					<span
						className={cn(
							'block truncate text-xs',
							clubNames.length > 0
								? 'text-dark'
								: 'italic text-gray-500',
						)}
						title={label}
					>
						{label}
					</span>
				);
			},
		},
		{
			header: 'CPF',
			accessor: 'cpf',
			headerClassName: TEXT_COLUMN_HEADER_CLASS,
			cellClassName: TEXT_COLUMN_CELL_CLASS,
			cell: (_, row) => (
				<span className="block truncate text-xs text-dark">
					{row.cpf ? maskCPF(row.cpf) : '—'}
				</span>
			),
		},
		{
			header: 'Telefone',
			accessor: 'phone_number',
			headerClassName: TEXT_COLUMN_HEADER_CLASS,
			cellClassName: TEXT_COLUMN_CELL_CLASS,
			cell: (value) => (
				<span className="block truncate text-xs text-dark">
					{value ? maskPhone(value as string) : '—'}
				</span>
			),
		},
		{
			header: 'Nasc.',
			accessor: 'birthday',
			headerClassName: TEXT_COLUMN_HEADER_CLASS,
			cellClassName: TEXT_COLUMN_CELL_CLASS,
			cell: (value) => (
				<span className="block truncate text-xs text-dark">
					{value ? formatDateWithDifference(value as string) : '—'}
				</span>
			),
		},
		{
			header: 'Ações',
			accessor: 'id',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (_, row) => (
				<IconColumnCell>
					<ActionsDropdown
						row={row}
						onEdit={onEdit}
						onToggleStatus={onToggleStatus}
					/>
				</IconColumnCell>
			),
		},
	];
};

export { UserColumns };
